import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { StatusTimeline } from "@/components/StatusTimeline";
import { meta } from "@/lib/statusMeta";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Camera, Download, Send, ImageOff, MessageCircle, Folder, ChevronLeft, ChevronRight, Share, Loader2 } from "lucide-react";

export default function CustomerPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [openFolder, setOpenFolder] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/portal/${token}`); setData(r.data); }
    catch { setNotFound(true); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setLightbox((lb) => lb ? { ...lb, index: (lb.index + 1) % lb.list.length } : lb);
      else if (e.key === "ArrowLeft") setLightbox((lb) => lb ? { ...lb, index: (lb.index - 1 + lb.list.length) % lb.list.length } : lb);
      else if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (notFound) return (
    <div className="min-h-screen relative z-[2] flex items-center justify-center p-8 text-center">
      <div><ImageOff className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="font-heading text-2xl font-bold">Order not found</h1>
        <p className="text-muted-foreground text-sm mt-2">This link may be invalid or expired.</p></div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground mono text-sm animate-pulse">Loading…</div>;

  const sendComment = async () => {
    if (!comment.trim()) return;
    try { await api.post(`/portal/${token}/comments`, { message: comment, name: name || undefined }); setComment(""); load(); toast.success("Message sent to the lab"); }
    catch { toast.error("Failed to send"); }
  };

  const downloadAll = () => { window.open(`${API}/portal/${token}/download-all`, "_blank"); };
  const imgUrl = (p) => `${API}/portal/${token}/photos/${p.id}`;

  const photos = data.photos || [];
  const folderNames = [];
  const folderMap = {};
  const loose = [];
  for (const p of photos) {
    if (p.folder) {
      if (!folderMap[p.folder]) { folderMap[p.folder] = []; folderNames.push(p.folder); }
      folderMap[p.folder].push(p);
    } else loose.push(p);
  }
  const folderPhotos = openFolder ? (folderMap[openFolder] || []) : [];
  const openScan = (list, index) => setLightbox({ list, index });
  const navScan = (dir) => setLightbox((lb) => lb ? { ...lb, index: (lb.index + dir + lb.list.length) % lb.list.length } : lb);
  const current = lightbox ? lightbox.list[lightbox.index] : null;
  const canShare = typeof navigator !== "undefined" && !!navigator.canShare;
  const downloadFolder = (fname) => { window.open(`${API}/portal/${token}/download-all?folder=${encodeURIComponent(fname)}`, "_blank"); };

  const saveToPhotos = async (p) => {
    try {
      const res = await fetch(imgUrl(p));
      const blob = await res.blob();
      const file = new File([blob], p.original_filename, { type: blob.type || "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: p.original_filename });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = p.original_filename; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't open share sheet"); }
  };

  const saveAllToPhotos = async (list) => {
    setSavingAll(true);
    try {
      const files = await Promise.all(list.map(async (p) => {
        const res = await fetch(imgUrl(p));
        const blob = await res.blob();
        return new File([blob], p.original_filename, { type: blob.type || "image/jpeg" });
      }));
      if (navigator.canShare && navigator.canShare({ files })) await navigator.share({ files, title: openFolder || "Photos" });
      else toast.error("This device can't save multiple photos at once — use Download instead");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't open share sheet"); }
    finally { setSavingAll(false); }
  };

  return (
    <div className="min-h-screen relative z-[2]">
      <header className="border-b border-border backdrop-blur-md bg-background/80 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand flex items-center justify-center"><Camera className="w-5 h-5 text-primary-foreground" /></div>
          <div><div className="font-heading font-bold text-[15px] leading-none">Low Five Film Lab</div>
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Your Order</div></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mono">{data.order_number}</h1>
            <p className="text-muted-foreground mt-1">Hi {data.customer_name}, here's the latest on your film.</p>
          </div>
          <span className="px-3 py-1.5 rounded-full text-sm font-semibold shrink-0"
            style={{ background: meta(data.status).color + "22", color: meta(data.status).color }}>{meta(data.status).label}</span>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 sm:p-8 mb-6">
          <StatusTimeline steps={data.steps} current={data.status} history={data.status_history} />
        </div>

        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold">Your Photos</h2>
            {photos.length > 0 && (
              <Button onClick={downloadAll} className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="customer-download-zip-btn">
                <Download className="w-4 h-4 mr-1.5" /> Download all (.zip)
              </Button>
            )}
          </div>
          {photos.length ? (
            <>
              <div className="space-y-4" data-testid="customer-photo-gallery">
                {folderNames.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {folderNames.map((fname) => {
                      const items = folderMap[fname];
                      return (
                        <button key={fname} onClick={() => setOpenFolder(fname)} data-testid={`customer-folder-${fname}`}
                          className="group text-left rounded-lg border border-border bg-secondary hover:border-brand/50 transition-colors p-4 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-md bg-brand/15 flex items-center justify-center shrink-0">
                            <Folder className="w-5 h-5 text-brand" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{fname}</div>
                            <div className="mono text-[11px] text-muted-foreground">{items.length} photo{items.length === 1 ? "" : "s"}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {loose.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {loose.map((p, i) => (
                      <button key={p.id} onClick={() => openScan(loose, i)} className="group aspect-square rounded-lg overflow-hidden border border-border bg-secondary relative">
                        <img src={imgUrl(p)} alt={p.original_filename} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="mono text-[11px] text-muted-foreground mt-4">Your photos remain available for 30 days. Please download and back them up.</p>
            </>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <ImageOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Your digital scans aren't ready yet. We'll email you the moment they are.</p>
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2 mb-4"><MessageCircle className="w-5 h-5 text-brand" /> Questions for the lab</h2>
          <div className="space-y-3 mb-4 max-h-72 overflow-y-auto" data-testid="portal-comments">
            {data.comments?.length ? data.comments.map((c) => (
              <div key={c.id} className={`flex ${c.role === "staff" ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[80%] rounded-xl px-4 py-2.5 ${c.role === "staff" ? "bg-secondary" : "bg-brand text-primary-foreground"}`}>
                  <div className="text-[11px] font-semibold opacity-70 mb-0.5">{c.role === "staff" ? "Lab staff" : c.name}</div>
                  <div className="text-sm whitespace-pre-wrap">{c.message}</div>
                </div>
              </div>
            )) : <p className="text-sm text-muted-foreground">No messages yet. Ask us anything about your order.</p>}
          </div>
          <div className="space-y-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)"
              className="w-full bg-secondary rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" data-testid="portal-name-input" />
            <div className="flex gap-2">
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a question or comment…" rows={2} data-testid="portal-comment-input" />
              <Button onClick={sendComment} className="bg-brand hover:bg-brand-hover text-primary-foreground self-end" data-testid="portal-comment-send"><Send className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </main>

      {/* Folder viewer */}
      <Dialog open={!!openFolder} onOpenChange={(v) => !v && setOpenFolder(null)}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="customer-folder-viewer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Folder className="w-5 h-5 text-brand" /> {openFolder}</DialogTitle>
            <DialogDescription>{folderPhotos.length} photo{folderPhotos.length === 1 ? "" : "s"} · tap a photo to view full size</DialogDescription>
          </DialogHeader>
          {openFolder && folderPhotos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="bg-brand hover:bg-brand-hover text-primary-foreground" onClick={() => downloadFolder(openFolder)} data-testid="customer-download-folder-btn">
                <Download className="w-4 h-4 mr-1.5" /> Download this folder (.zip)
              </Button>
              {canShare && (
                <Button size="sm" variant="outline" onClick={() => saveAllToPhotos(folderPhotos)} disabled={savingAll} data-testid="customer-save-all-to-photos-btn">
                  {savingAll ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Share className="w-4 h-4 mr-1.5" />} Save all to Photos
                </Button>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {folderPhotos.map((p, i) => (
              <button key={p.id} onClick={() => openScan(folderPhotos, i)} className="group aspect-square rounded-lg overflow-hidden border border-border bg-secondary relative" data-testid={`customer-folder-photo-${p.id}`}>
                <img src={imgUrl(p)} alt={p.original_filename} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox with swipe */}
      <Dialog open={!!current} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="bg-card border-border max-w-3xl p-2" data-testid="customer-lightbox">
          <DialogHeader className="sr-only">
            <DialogTitle>Photo preview</DialogTitle>
            <DialogDescription>View, swipe and download this photo</DialogDescription>
          </DialogHeader>
          {current && (
            <div className="flex flex-col">
              <div className="relative flex items-center justify-center"
                onTouchStart={(e) => { e.currentTarget._x = e.touches[0].clientX; }}
                onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (e.currentTarget._x || 0); if (Math.abs(dx) > 50) navScan(dx < 0 ? 1 : -1); }}>
                <img src={imgUrl(current)} alt={current.original_filename} className="w-full max-h-[75vh] object-contain rounded-lg" />
                {lightbox.list.length > 1 && (
                  <>
                    <button onClick={() => navScan(-1)} data-testid="customer-lightbox-prev"
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/80 backdrop-blur flex items-center justify-center hover:bg-background">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={() => navScan(1)} data-testid="customer-lightbox-next"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/80 backdrop-blur flex items-center justify-center hover:bg-background">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center justify-between p-3 gap-3">
                <span className="mono text-xs text-muted-foreground truncate">
                  {current.original_filename}{lightbox.list.length > 1 ? ` · ${lightbox.index + 1}/${lightbox.list.length}` : ""}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {canShare && (
                    <Button size="sm" variant="outline" onClick={() => saveToPhotos(current)} data-testid="customer-save-to-photos-btn">
                      <Share className="w-4 h-4 mr-1.5" /> Save to Photos
                    </Button>
                  )}
                  <a href={imgUrl(current)} download={current.original_filename} target="_blank" rel="noreferrer">
                    <Button size="sm" className="bg-brand hover:bg-brand-hover text-primary-foreground"><Download className="w-4 h-4 mr-1.5" /> Download</Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
